import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Truck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { OrderDetailCard } from './components/OrderDetailCard'
import { DeliveryDialog } from './components/DeliveryDialog'
import { useSupplierOrder, useOrderDelivery, useOrderSplitDelivery } from '@/hooks/useSupplierOrder'
import { showError } from '@/lib/error'
import { ORDER_STATUS, type DeliveryParams, type SplitDeliveryParams } from '@/schemas/supplier'

export function SupplierOrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const orderId = Number(id)
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false)

  // 查询订单详情
  const { data: order, isLoading } = useSupplierOrder(orderId)

  // 发货 mutation
  const deliveryMutation = useOrderDelivery()
  const splitDeliveryMutation = useOrderSplitDelivery()

  // 是否可以发货
  const canDeliver = order && order.status === ORDER_STATUS.PENDING_SHIPMENT

  // 处理发货
  const handleDelivery = (data: DeliveryParams | SplitDeliveryParams) => {
    const onSuccess = () => {
      setShowDeliveryDialog(false)
      navigate('/supplier/orders')
    }
    if ('cart_ids' in data) {
      splitDeliveryMutation.mutate(
        { id: orderId, data },
        { onSuccess, onError: (error) => showError(error, '发货失败') }
      )
    } else {
      deliveryMutation.mutate(
        { id: orderId, data },
        { onSuccess, onError: (error) => showError(error, '发货失败') }
      )
    }
  }

  // 加载中
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/supplier/orders')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    )
  }

  // 订单不存在
  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground mb-4">订单不存在</p>
        <Button onClick={() => navigate('/supplier/orders')}>
          返回订单列表
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/supplier/orders')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">订单详情</h1>
            <p className="text-muted-foreground">订单号：{order.order_id}</p>
          </div>
        </div>
        {canDeliver && (
          <Button onClick={() => setShowDeliveryDialog(true)}>
            <Truck className="mr-1.5 h-4 w-4" />
            发货
          </Button>
        )}
      </div>

      {/* 订单详情 */}
      <OrderDetailCard order={order} />

      {/* 发货对话框 */}
      <DeliveryDialog
        order={order}
        open={showDeliveryDialog}
        onOpenChange={setShowDeliveryDialog}
        onConfirm={handleDelivery}
        isLoading={deliveryMutation.isPending || splitDeliveryMutation.isPending}
      />
    </div>
  )
}

export default SupplierOrderDetailPage
